import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import { EditActions, editActions } from "../../actions/editActions";
import { hasWriteAccess, nameofFactory } from "../../utils/utils";
import { escalatorRTDGroupHasChildren } from "../../utils/pdApiUtils";
import Editor from "../../components/Editing/Editor";
import NumberField from "../../components/Editing/NumberField";
import TextField from "../../components/Editing/TextField";
import { Divider, Modal } from "semantic-ui-react";
import ButtonField from "../../components/Editing/ButtonField";
import { EditLocalizationStatus } from "./EditLocalizationStatus";
import TextIndicatorField from "../../components/Editing/TextIndicatorField";

type StateProps = {
  editedEscalatorRTDGroup: Api.EscalatorRTDGroup;
  storedEscalatorRTDGroup: Api.EscalatorRTDGroup;
  pdApi: State.PdApi;
  activeUser?: Api.User;
  columns: State.ColumnMap;
  isShortenTextEnabled: boolean;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
};

export type EditEscalatorRTDGroupProps = StateProps & DispatchProps & OwnProps;

export type EditEscalatorRTDGroupState = {
  isEditLocalizationStatusOpen: boolean;
  locsOriginal: Api.LocsEscalatorRTDGroup[];
  locsEdited: Api.LocsEscalatorRTDGroup[];
};

const escalatorRTDGroupFields = nameofFactory<Api.EscalatorRTDGroup>();

export class EditEscalatorRTDGroup extends React.Component<
  EditEscalatorRTDGroupProps,
  EditEscalatorRTDGroupState
> {
  constructor(props: EditEscalatorRTDGroupProps) {
    super(props);
    this.state = {
      isEditLocalizationStatusOpen: false,
      locsOriginal: [],
      locsEdited: []
    };
  }
  public render(): JSX.Element {
    const { isEditLocalizationStatusOpen } = this.state;
    const {
      editedEscalatorRTDGroup,
      storedEscalatorRTDGroup,
      activeUser,
      columns,
      pdApi,
      toggleModal,
      isShortenTextEnabled
    } = this.props;
    const { escalatorRTDs } = pdApi;

    const isNew = !storedEscalatorRTDGroup;
    const permissions = hasWriteAccess(activeUser, "Escalator");
    const deleteDisabled =
      (storedEscalatorRTDGroup &&
        escalatorRTDGroupHasChildren(storedEscalatorRTDGroup.enumValue, escalatorRTDs)) ||
      editedEscalatorRTDGroup.enumValue === 0;

    return (
      <Editor
        itemName={"escalator rtd group"}
        originalObject={storedEscalatorRTDGroup}
        editedObject={editedEscalatorRTDGroup}
        hasWritePermission={hasWriteAccess(activeUser, "Escalator")}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => {
          this.props.editActions.setEdited({
            ...editedEscalatorRTDGroup,
            locs: this.state.locsEdited
          });
          this.props.editActions.update("escalatorRTDGroups");
        }}
        create={() => {
          this.props.editActions.setEdited({
            ...editedEscalatorRTDGroup,
            locs: this.state.locsEdited
          });
          this.props.editActions.add("escalatorRTDGroups");
        }}
        copy={() => {
          this.props.editActions.setEdited({
            ...editedEscalatorRTDGroup,
            locs: this.state.locsEdited
          });
          this.props.editActions.startEditing(editedEscalatorRTDGroup, true);
        }}
        remove={{
          callback: toggleModal,
          disabledTooltip:
            editedEscalatorRTDGroup.enumValue === 0
              ? " because it is reserved as a default value for Escalator RTDs."
              : " as it is used in one or more Escalator RTDs.",
          disabled: deleteDisabled
        }}
      >
        <NumberField
          id="enumValue"
          label="Enum Value"
          value={editedEscalatorRTDGroup.enumValue}
          isError={!this.isFieldValid(escalatorRTDGroupFields("enumValue"))}
          errorMessage="The enum value must be unique."
          readonly={!isNew}
          onChange={enumValue =>
            this.props.editActions.setEdited({ ...editedEscalatorRTDGroup, enumValue })
          }
        />
        <TextField
          id="enumName"
          label="Enum Name"
          value={editedEscalatorRTDGroup.enumName}
          isError={!this.isFieldValid(escalatorRTDGroupFields("enumName"))}
          errorMessage={
            !this.isEmptyString(editedEscalatorRTDGroup.enumName)
              ? "The enum name must be unique."
              : ""
          }
          onChange={enumName =>
            this.props.editActions.setEdited({
              ...editedEscalatorRTDGroup,
              enumName: enumName.trim()
            })
          }
          readonly={!permissions}
        />
        <TextField
          id="description"
          label="Description"
          value={editedEscalatorRTDGroup.description}
          isError={!this.isFieldValid(escalatorRTDGroupFields("description"))}
          onChange={description =>
            this.props.editActions.setEdited({
              ...editedEscalatorRTDGroup,
              description: description.trim()
            })
          }
          readonly={!permissions}
        />
        <Divider horizontal={true}>{"English translations"}</Divider>
        <TextIndicatorField
          id={"needsUpdateIndicator"}
          text={"Edited texts will be translated."}
          isWarning={this.englishTranslationsChanged()}
        />
        <TextField
          id="englishName"
          label="English Name"
          value={editedEscalatorRTDGroup.name}
          isError={!this.isFieldValid(escalatorRTDGroupFields("name"))}
          onChange={value => {
            this.props.editActions.setEdited({
              ...editedEscalatorRTDGroup,
              name: value.trim()
            } as Api.EscalatorRTDGroup);
          }}
          readonly={!permissions}
        />
        <ButtonField
          id={"viewTranslationsButton"}
          label="View translations"
          onButtonClick={this.toggleEditLocalizationStatusModal}
        />
        <Modal size="large" open={isEditLocalizationStatusOpen} className="data-sections-modal">
          <EditLocalizationStatus
            itemType={"Escalator RTD Group"}
            editedItem={{ ...editedEscalatorRTDGroup, locs: this.state.locsEdited }}
            getOption="escalatorRTDGroupLocalizations"
            columns={columns["Escalator RTD Group Localization Status"]}
            pdApi={pdApi}
            toggleEditLocalizationStatusModal={this.toggleEditLocalizationStatusModal}
            setLocalizationsEdited={this.setLocalizationsEdited}
            isShortenTextEnabled={isShortenTextEnabled}
            englishTranslationChanged={this.englishTranslationsChanged()}
            userPermissions={permissions}
            locsOriginal={this.state.locsOriginal || []}
            setLocalizationsNonEdited={this.setLocalizationsNonEdited}
          />
        </Modal>
      </Editor>
    );
  }

  private englishTranslationsChanged = (): boolean =>
    this.props.editedEscalatorRTDGroup.name !== this.props.storedEscalatorRTDGroup?.name;

  private handleEnglishTranslationsChange(item: Api.EscalatorRTDGroup) {
    const { storedEscalatorRTDGroup, editedEscalatorRTDGroup } = this.props;
    const { locsOriginal, locsEdited } = this.state;

    // create both edited and stored item locs if needed
    const edited =
      locsEdited.length === 0
        ? [
            {
              text: item.name,
              localeNavigation: { locale: "en-US" }
            } as Api.LocsEscalatorRTDGroup
          ]
        : this.state.locsEdited.map(loc => {
            if (loc.localeNavigation.locale === "en-US") {
              return {
                ...loc,
                text: item.name
              } as Api.LocsEscalatorRTDGroup;
            } else {
              return loc;
            }
          });
    const original =
      locsOriginal.length === 0
        ? [
            {
              text: storedEscalatorRTDGroup?.name,
              localeNavigation: { locale: "en-US" }
            } as Api.LocsEscalatorRTDGroup
          ]
        : locsOriginal;

    this.setState((prevState: EditEscalatorRTDGroupState) => ({
      ...prevState,
      locsEdited: edited,
      locsOriginal: original
    }));
    this.props.editActions.setEdited({
      ...editedEscalatorRTDGroup,
      name: item.name
    });
  }

  private toggleEditLocalizationStatusModal = (): void => {
    this.setState((prevState: EditEscalatorRTDGroupState) => ({
      ...prevState,
      isEditLocalizationStatusOpen: !prevState.isEditLocalizationStatusOpen
    }));
  };

  private setLocalizationsEdited = (
    newLocalizations: Api.LocsEscalatorRTDGroup[],
    oldLocalizations: Api.LocsEscalatorRTDGroup[]
  ): void => {
    const { editedEscalatorRTDGroup } = this.props;
    this.setState((prevState: EditEscalatorRTDGroupState) => ({
      ...prevState,
      locsEdited: newLocalizations,
      locsOriginal: oldLocalizations
    }));
    this.props.editActions.setEdited({
      ...editedEscalatorRTDGroup,
      name:
        newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.text ||
        editedEscalatorRTDGroup?.name,
      locs: newLocalizations
    } as Api.EscalatorRTDGroup);
  };

  private setLocalizationsNonEdited = (Localizations: Api.LocsEscalatorRTDGroup[]): void => {
    this.setState((prevState: EditEscalatorRTDGroupState) => ({
      ...prevState,
      locsOriginal: Localizations
    }));
  };

  private isFieldValid = (field: string): boolean => {
    const { editedEscalatorRTDGroup, storedEscalatorRTDGroup, pdApi } = this.props;
    const isNew = !storedEscalatorRTDGroup;
    if (!Number.isInteger(editedEscalatorRTDGroup.enumValue)) return false;
    switch (field) {
      case escalatorRTDGroupFields("enumValue"):
        return _.values(pdApi.escalatorRTDGroups).find(
          escalatorRTDGroup =>
            (isNew || escalatorRTDGroup.id !== editedEscalatorRTDGroup.id) &&
            escalatorRTDGroup.enumValue === editedEscalatorRTDGroup.enumValue
        )
          ? false
          : true;
      case escalatorRTDGroupFields("enumName"): {
        const { enumName } = editedEscalatorRTDGroup;
        if (this.isEmptyString(enumName)) {
          return false;
        } else {
          return _.values(pdApi.escalatorRTDGroups).find(
            escalatorRTDGroup =>
              (isNew || escalatorRTDGroup.id !== editedEscalatorRTDGroup.id) &&
              escalatorRTDGroup.enumName?.trim().toLowerCase() ===
                editedEscalatorRTDGroup.enumName?.trim().toLowerCase()
          )
            ? false
            : true;
        }
      }
      case escalatorRTDGroupFields("description"): {
        const { description } = editedEscalatorRTDGroup;
        return !this.isEmptyString(description);
      }
      case escalatorRTDGroupFields("name"): {
        const { name } = editedEscalatorRTDGroup;
        return !this.isEmptyString(name);
      }
      default:
        return true;
    }
  };

  private isEmptyString = (str: string): boolean => !(!!str.trim() && str.trim().length > 0);

  private isAllFieldsValid = (): boolean => {
    const { editedEscalatorRTDGroup } = this.props;
    const temp = _.keys(editedEscalatorRTDGroup);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedEscalatorRTDGroup: state.editView.editedObject,
  storedEscalatorRTDGroup: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser,
  isShortenTextEnabled: state.main.isShortenTextEnabled
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditEscalatorRTDGroup);
